import React, { useMemo } from "react";
import { observer } from "mobx-react";
import { useStore } from "../../../../../../hooks";
import {
  Chip,
  Autocomplete,
  TextField,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

import {
  StyledFormControl,
  ItemName,
  ItemDescription,
  ChipContent,
  ChipName,
  ChipValue,
  ItemWrapper,
  MultiSelectorGlobalStyle
} from './MultiSelector.styled';
import { FilterTypes } from '../../../../../../utils/enums';

const MultiSelector = observer(({
  onChange,
  customerId,
  selectedUserIds = [],
  placeholder = "Search and Select",
  filterType = null,
  disabled = false,
  isLoading = false
}) => {
  const { marketingStore } = useStore();
  const { advancedSettingsStore } = marketingStore;

  const items = useMemo(() => {
    if (!filterType) return [];
    
    switch (filterType) {
      case FilterTypes.USER:
        return advancedSettingsStore.usersItems || [];
      case FilterTypes.GROUP:
        return advancedSettingsStore.groupsItems || [];
      case FilterTypes.ROLE:
        return advancedSettingsStore.rolesItems || [];
      default:
        return [];
    }
  }, [
    advancedSettingsStore.usersItems,
    advancedSettingsStore.groupsItems,
    advancedSettingsStore.rolesItems,
    filterType
  ]);

  const mappedItems = useMemo(() => {
    if (filterType === FilterTypes.USER) {
      return items.map((u) => ({
        id: u.id,
        name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
        value: u.role?.name || '',
        type: FilterTypes.USER,
      }));
    }
    else if (filterType === FilterTypes.GROUP) {
      return items
        .map((g) => ({
          id: g.id,
          name: g.name,
          value: `${g.users_count} users`,
          type: FilterTypes.GROUP,
        }))
        .filter(g => !g.value.startsWith('0'));
    }
    else if (filterType === FilterTypes.ROLE) {
      return items
        .map((r) => ({
          id: r.id,
          name: r.name,
          value: '',
          type: FilterTypes.ROLE,
        }));
    }
    
    return [];
  }, [items, filterType]);

  const selectedItems = useMemo(() => {
    return selectedUserIds.map(id => mappedItems.find(item => item.id === id)).filter(Boolean);
  }, [selectedUserIds, mappedItems]);

  const handleChange = (event, newValue) => {
    onChange(newValue.map(item => item.id));
  };

  return (
    <>
    <MultiSelectorGlobalStyle />
    <StyledFormControl data-testid="styled-form-control">
      <Autocomplete
        multiple
        id={`multi-select-${filterType || 'all'}`}
        options={mappedItems}
        value={selectedItems}
        onChange={handleChange}
        disabled={disabled || isLoading}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        popupIcon={selectedItems.length === 0 ? 
          (isLoading ? <CircularProgress size={20} /> : <SearchIcon className="search-icon"/>) 
          : null
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={selectedItems.length === 0 ? placeholder : ''}
            variant="outlined"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props}>
            <ItemWrapper>
              <ItemName variant="body1">{option.name}</ItemName>
              {option.value && <ItemDescription variant="body2">{option.value}</ItemDescription>}
            </ItemWrapper>
          </li>
        )}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={
                <ChipContent>
                  <ChipName>{option.name}</ChipName>
                  {option.value && <ChipValue>{option.value}</ChipValue>}
                </ChipContent>
              }
            />
          ))
        }
      />
    </StyledFormControl>
    </>
  );
});

export default MultiSelector;